/** UC1 stage 4: settling a conflicting field by choosing a value one of the documents gave. */
import type { Candidate } from "../components/Mismatch";
import { DOC_TYPE_LABELS, FIELD_LABELS, type FieldOverride, type Job, type Result, type UC5Job } from "../lib/api";
import { ApiError, apiGet, apiSend } from "../lib/http";
import { fileName } from "../lib/words";

/** The job is reading its confirmed documents. */
const READING = ["parsing", "extracting"];
/** The job's extraction has been stored, so it has a result to show. */
const EXTRACTED = ["awaiting_review", "completed"];

/** Whether the server should already hold a result for this job. */
export function resultExpected(job: Job | null): boolean {
  return !!job && EXTRACTED.includes(job.status);
}

/**
 * Keyed on the status as well as the id. Keyed on the id alone, the result was asked for once
 * while the job was still reading, answered 404, and the summary stayed blank until a reload.
 */
export function resultQueryKey(job: Job): readonly unknown[] {
  return ["uc1-result", job.id, job.status];
}

/**
 * The status can say finished a moment before the result is readable, so a 404 is asked again
 * for about half a minute (one second apart). Any other refusal is an answer.
 */
export function retryResult(count: number, error: unknown): boolean {
  return error instanceof ApiError && error.status === 404 && count < 30;
}

export function loadResult(jobId: string): Promise<Result> {
  return apiGet("/api/v1/uc1/jobs/{job_id}/result", { path: { job_id: jobId } }) as unknown as Promise<Result>;
}

/** Start a vendor and obsolescence check from a finished extraction, with its chosen values. */
export async function startVendorCheck(jobId: string): Promise<UC5Job> {
  return (await apiSend("/api/v1/uc5/jobs", { from_job_id: jobId } as never)) as unknown as UC5Job;
}

/**
 * The step after a finished extraction: open the vendor check already started from it (the
 * newest), or start one. Nothing before the extraction has finished.
 */
export function nextStep(job: Job, checks: UC5Job[]): { kind: "open"; id: string } | { kind: "start" } | null {
  if (!resultExpected(job)) return null;
  const started = checks.filter((c) => c.source_job_id === job.id);
  // GET /uc5/jobs lists newest first.
  return started.length ? { kind: "open", id: started[0].id } : { kind: "start" };
}

export interface ExtractionView {
  /** The confirm-and-read card is shown. Never once a read has started: that would buy it twice. */
  offerRead: boolean;
  readDisabled: boolean;
  readLabel: string;
  /** none: nothing read yet; loading: read finished, result on its way; ready: shown. */
  summary: "none" | "loading" | "ready";
}

/** What the extraction screen offers for a job, from its status and what the screen holds. */
export function extractionView(
  job: Job, { selected, sending, hasResult }: { selected: number; sending: boolean; hasResult: boolean },
): ExtractionView {
  const underWay = READING.includes(job.status) || EXTRACTED.includes(job.status);
  const offerRead = (job.candidates?.length ?? 0) > 0 && !job.selected_document && !underWay;
  return {
    offerRead,
    readDisabled: sending || selected === 0,
    readLabel: sending ? "Reading..." : `Read ${selected} document${selected === 1 ? "" : "s"}`,
    summary: resultExpected(job) ? (hasResult ? "ready" : "loading") : "none",
  };
}

export interface Conflict {
  /** The field name the API knows it by. */
  name: string;
  label: string;
  candidates: Candidate[];
  /** The choice in force, or null while nobody has chosen. */
  chosen: FieldOverride | null;
}

/**
 * Each field where documents disagree, with every value and the page it came from. A settled
 * conflict stays listed with its choice, so it can be seen and changed.
 */
export function conflictsOf(result: Result | null): Conflict[] {
  return (result?.fields ?? [])
    .filter((f) => f.in_conflict && f.alternatives.length > 1)
    .map((f) => ({
      name: f.name,
      label: FIELD_LABELS[f.name] ?? f.name.replace(/_/g, " "),
      chosen: f.override ?? null,
      candidates: f.alternatives.map((a) => ({
        value: a.value,
        documentId: a.document_id,
        documentPath: a.source_path,
        documentName: `${fileName(a.source_path)} (${DOC_TYPE_LABELS[a.doc_type] ?? a.doc_type})`,
        page: a.page_number ?? 1,
        confidence: a.confidence,
        box: a.box,
      })),
    }));
}

/** Record the choice. The chooser is the signed-in person; the API takes it from the session. */
export async function chooseValue(
  jobId: string, field: string, pick: { value: string; documentId: string | null }, note: string,
): Promise<Result> {
  const trimmed = note.trim();
  return (await apiSend(
    "/api/v1/uc1/jobs/{job_id}/fields/{field}/override",
    { value: pick.value, document_id: pick.documentId, ...(trimmed ? { note: trimmed } : {}) },
    { path: { job_id: jobId, field } },
  )) as unknown as Result;
}
